import { siteConfig } from './siteConfig';
import { cattleFaqs, farmerFaqs, generalFaqs, oilFaqs, pigFaqs, qualityFaqs, type FAQItem } from './faqs';
import { blogPosts } from './blogData';

export type SeoSchemaType = 'WebPage' | 'AboutPage' | 'ContactPage' | 'CollectionPage' | 'Product' | 'Article' | 'BlogPosting';

export type SeoRoute = {
  path: string;
  title: string;
  metaTitle?: string;
  description: string;
  canonicalPath?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product';
  schemaType?: SeoSchemaType;
  faqs?: FAQItem[];
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
  product?: {
    name: string;
    category: string;
    image?: string;
    description?: string;
    brand?: string;
    sku?: string;
    priceCurrency?: string;
    availability?: string;
  };
};

export const defaultOgImage = '/zfast-logo.webp';

export const seoRoutes: SeoRoute[] = [
  {
    path: '/',
    title: 'ZFAST Agritech India | Cattle Feed Manufacturer, Pig Feed & Edible Oils',
    description:
      'ZFAST Agritech India is a premier agricultural manufacturer in Jaipur, Rajasthan, supporting Indian farmers, dealers, and distributors with balanced cattle feed, pig feed, and pure edible oils.',
    schemaType: 'WebPage',
    faqs: generalFaqs
  },
  {
    path: '/about',
    title: 'About ZFAST Agritech India | Animal Nutrition & Agritech Supply',
    description:
      'Learn about ZFAST Agritech India, an agricultural manufacturing company in Jaipur supporting dairy farmers, swine breeders, and edible oil supply across India.',
    schemaType: 'AboutPage',
    faqs: generalFaqs
  },
  {
    path: '/our-purpose',
    title: 'Our Purpose | ZFAST Agritech India',
    description:
      'ZFAST Agritech India supports practical farm growth, responsible agritech supply, dealer trust, and clear product conversations for Indian agriculture.',
    schemaType: 'AboutPage'
  },
  {
    path: '/courage-to-care',
    title: 'Courage to Care | ZFAST Agritech India',
    description:
      'Learn about ZFAST Agritech India values, people-first thinking, responsible growth, and care for future farming generations.',
    schemaType: 'AboutPage'
  },
  {
    path: '/our-story',
    title: 'Our Story | ZFAST Agritech India',
    description:
      'Read the ZFAST Agritech India story, built around farmer relationships, animal nutrition, edible oil manufacturing, product quality, and dealer trust.',
    schemaType: 'AboutPage'
  },
  {
    path: '/products',
    title: 'Animal Feed & Edible Oil Products | ZFAST Agritech India',
    description:
      'Explore ZFAST cattle feed (Z-10000, Z-30, Z-8000), complete swine/pig feed range, and pure edible oils (Mustard, Soya, Sunflower) for farms and wholesale distributors.',
    schemaType: 'CollectionPage',
    faqs: [...cattleFaqs.slice(0, 2), ...pigFaqs.slice(0, 2), ...oilFaqs.slice(0, 2)]
  },
  {
    path: '/animal-nutrition',
    title: 'Animal Nutrition for Indian Farms | Cattle & Pig Feed | ZFAST Agritech',
    description:
      'Scientifically formulated cattle feed and swine nutrition for Indian farms. Balanced protein, energy, bypass fat, and essential minerals for animal health and productivity.',
    schemaType: 'CollectionPage',
    faqs: [...cattleFaqs.slice(0, 3), ...pigFaqs.slice(0, 2)]
  },
  {
    path: '/animal-nutrition/cattle-feed',
    title: 'Cattle Feed for Indian Dairy Farms | Dairy Cow Nutrition | ZFAST',
    description:
      'ZFAST cattle feed provides high-protein, balanced energy, bypass nutrition, and aflatoxin-controlled formulations for dairy cows and buffaloes across India.',
    ogType: 'product',
    schemaType: 'Product',
    faqs: cattleFaqs,
    product: {
      name: 'ZFAST Dairy Cattle Feed Range',
      category: 'Animal Nutrition / Cattle Feed',
      image: '/images/products/cattle-feed/cattle-feed.webp',
      description:
        'ZFAST cattle feed supports dairy farm nutrition, animal condition support, dealer enquiries, and practical Indian farming conditions.',
      sku: 'ZFAST-CATTLE-RANGE',
      priceCurrency: 'INR',
      availability: 'https://schema.org/InStock'
    }
  },
  {
    path: '/animal-nutrition/pig-feed',
    title: 'Pig Feed for Swine Farms | Stage-Wise Pig Nutrition | ZFAST',
    description:
      'Complete stage-wise pig feed (Starter, Grower, Finisher, Gestation, Lactation) for commercial swine farms, piglet immunity, and feed efficiency in India.',
    ogType: 'product',
    schemaType: 'Product',
    faqs: pigFaqs,
    product: {
      name: 'ZFAST Stage-Wise Swine Feed Range',
      category: 'Animal Nutrition / Pig Feed',
      image: '/images/products/pig-feed/zfast-pig-feed.webp',
      description:
        'ZFAST pig feed supports stage-wise swine nutrition, grower routines, breeder planning, and dealer supply conversations.',
      sku: 'ZFAST-PIG-RANGE',
      priceCurrency: 'INR',
      availability: 'https://schema.org/InStock'
    }
  },
  {
    path: '/products/z-8000',
    title: 'ZFAST 8000 Cattle Feed | Reliable Daily Nutrition for Dairy Cattle',
    description:
      'ZFAST 8000 is a balanced cattle feed pellet for everyday dairy farm nutrition, maintenance, body condition support, and steady milk yields.',
    ogType: 'product',
    schemaType: 'Product',
    faqs: cattleFaqs.slice(0, 3),
    product: {
      name: 'ZFAST 8000 Cattle Feed',
      category: 'Cattle Feed',
      image: '/images/products/cattle-feed/z-8000-pro-detail.webp',
      description:
        'ZFAST 8000 is a balanced cattle feed for everyday dairy farm nutrition, body condition support, and practical feeding routines.',
      sku: 'ZFAST-8000',
      priceCurrency: 'INR',
      availability: 'https://schema.org/InStock'
    }
  },
  {
    path: '/products/z-30-pro',
    title: 'Z-30 Pro Cattle Feed | Enhanced Productivity Nutrition | ZFAST',
    description:
      'Z-30 Pro cattle feed supports productive dairy animals with enhanced protein, energy density, and mineral fortification for standard-to-high yielding cows.',
    ogType: 'product',
    schemaType: 'Product',
    faqs: cattleFaqs.slice(0, 3),
    product: {
      name: 'Z-30 Pro Cattle Feed',
      category: 'Cattle Feed',
      image: '/images/products/cattle-feed/z-30-pro-detail-new.webp',
      description:
        'Z-30 cattle feed supports productive dairy animals with balanced nutrition, energy support, and practical feeding guidance.',
      sku: 'ZFAST-30-PRO',
      priceCurrency: 'INR',
      availability: 'https://schema.org/InStock'
    }
  },
  {
    path: '/products/z-10000',
    title: 'ZFAST 10000 Cattle Feed | Premium High-Yield Dairy Nutrition',
    description:
      'ZFAST 10000 is our flagship premium cattle feed formulated for commercial high-yield dairy animals, peak lactation support, and growth.',
    ogType: 'product',
    schemaType: 'Product',
    faqs: cattleFaqs,
    product: {
      name: 'ZFAST 10000 Premium Cattle Feed',
      category: 'Cattle Feed',
      image: '/images/products/cattle-feed/z-10000-pro-detail.webp',
      description:
        'ZFAST 10000 cattle feed is positioned for high-yield dairy animals, growth support, and commercial dairy nutrition planning.',
      sku: 'ZFAST-10000',
      priceCurrency: 'INR',
      availability: 'https://schema.org/InStock'
    }
  },
  ...[
    ['pig-starter', 'Pig Starter Feed', 'Stage-specific piglet feed supporting early post-weaning growth, gut health, immunity, and high feed conversion.', 'zfast-starter.webp', 'ZFAST-PIG-STARTER'],
    ['pig-grower', 'Pig Grower Feed', 'Grower-stage swine feed supporting steady skeletal growth, body condition, digestion, and feed efficiency.', 'zfast-grower.webp', 'ZFAST-PIG-GROWER'],
    ['pig-finisher', 'Pig Finisher Feed', 'Finisher-stage swine feed supporting rapid weight gain, lean meat quality, gut health, and feed conversion.', 'zfast-finisher.webp', 'ZFAST-PIG-FINISHER'],
    ['sow-gestation', 'Sow Gestation Feed', 'Breeder sow feed supporting optimal body condition, fetal development, immunity, and reproductive longevity.', 'sow-gestation.webp', 'ZFAST-SOW-GEST'],
    ['sow-lactation', 'Sow Lactation Feed', 'Breeder feed supporting high milk production, maternal condition, immunity, and healthy weaned litters.', 'sow-lactation.webp', 'ZFAST-SOW-LACT']
  ].map(([slug, name, description, image, sku]) => ({
    path: `/products/${slug}`,
    title: `${name} | ZFAST Swine Nutrition`,
    description,
    ogType: 'product' as const,
    schemaType: 'Product' as const,
    faqs: pigFaqs,
    product: {
      name,
      category: 'Pig Feed',
      image: `/images/products/${image}`,
      description,
      sku,
      priceCurrency: 'INR',
      availability: 'https://schema.org/InStock'
    }
  })),
  {
    path: '/edible-oils',
    title: 'Edible Oils Suppliers | Mustard, Soybean & Sunflower Oil Wholesale | ZFAST',
    description:
      'ZFAST Agritech supplies pure Kachi Ghani Mustard Oil, Refined Soybean Oil, and Sunflower Oil across India for retail, food service, and bulk wholesale.',
    schemaType: 'CollectionPage',
    faqs: oilFaqs
  },
  {
    path: '/edible-oils/mustard-oil',
    title: 'Z-Strong Mustard Oil | Pure Kachi Ghani Mustard Oil | ZFAST',
    description:
      'Z-Strong Kachi Ghani mustard oil with authentic pungent aroma and natural purity. Enquiries for retail packs, 15L tins, and wholesale bulk distributors.',
    ogType: 'product',
    schemaType: 'Product',
    faqs: oilFaqs,
    product: {
      name: 'Z-Strong Mustard Oil',
      category: 'Edible Oil',
      image: '/images/products/edible-oils/oil-mustard.webp',
      description:
        'Z-strong mustard oil enquiry support for traditional Indian cooking, retail interest, distributor conversations, and bulk buyers.',
      sku: 'ZFAST-OIL-MUSTARD',
      priceCurrency: 'INR',
      availability: 'https://schema.org/InStock'
    }
  },
  {
    path: '/edible-oils/soybean-oil',
    title: 'Refined Soybean Oil Suppliers | Cooking & Food Service Oil | ZFAST',
    description:
      'Premium refined soybean cooking oil for food service, institutional kitchens, consumer packs, and bulk edible oil buyers in India.',
    ogType: 'product',
    schemaType: 'Product',
    faqs: oilFaqs,
    product: {
      name: 'ZFAST Soybean Refined Oil',
      category: 'Edible Oil',
      image: '/images/products/edible-oils/oil-soybean.webp',
      description:
        'Premium soya oil for cooking and soybean refined oil. Enquiries for food service, institutional kitchens, retail packs, and bulk edible oil buyers.',
      sku: 'ZFAST-OIL-SOYBEAN',
      priceCurrency: 'INR',
      availability: 'https://schema.org/InStock'
    }
  },
  {
    path: '/edible-oils/sunflower-oil',
    title: 'Pure Sunflower Oil Wholesale | Deep Frying & Cooking Oil | ZFAST',
    description:
      'Light and heart-friendly sunflower oil for deep frying, commercial food preparation, retail dealerships, and bulk institutional supply.',
    ogType: 'product',
    schemaType: 'Product',
    faqs: oilFaqs,
    product: {
      name: 'ZFAST Sunflower Oil',
      category: 'Edible Oil',
      image: '/images/products/edible-oils/oil-sunflower.webp',
      description:
        'ZFAST sunflower oil for deep frying suppliers, food service, and everyday cooking. Retail interest, dealer supply, and bulk edible oil requirements.',
      sku: 'ZFAST-OIL-SUNFLOWER',
      priceCurrency: 'INR',
      availability: 'https://schema.org/InStock'
    }
  },
  {
    path: '/quality-research',
    title: 'Quality & Research | Aflatoxin Testing & Batch Discipline | ZFAST',
    description:
      'Learn how ZFAST ensures feed safety through rigorous raw material selection, lab proximate analysis, aflatoxin screening, and storage discipline.',
    schemaType: 'WebPage',
    faqs: qualityFaqs
  },
  {
    path: '/dealer-partnership',
    title: 'Dealer & Distributor Partnership | Become a ZFAST Partner',
    description:
      'Partner with ZFAST Agritech India for cattle feed, pig feed, and edible oil distributorships. Enjoy reliable supply, marketing support, and dealer trust.',
    schemaType: 'WebPage',
    faqs: generalFaqs
  },
  {
    path: '/farmer-corner',
    title: 'Farmer Corner | Cattle & Pig Weight Calculators & Nutrition Guides | ZFAST',
    description:
      'Free farmer tools: Cattle Weight Calculator, Pig Weight Calculator, Body Condition Score (BCS) charts, AI guidelines, and practical feeding guidance.',
    schemaType: 'WebPage',
    faqs: farmerFaqs
  },
  {
    path: '/aflatoxin-controlled-feed',
    title: 'Aflatoxin-Controlled Feed Approach | Safe Feed Standards | ZFAST',
    description:
      'Discover ZFAST strict aflatoxin control protocols, grain testing, moisture management, toxin binder discipline, and animal safety assurance.',
    schemaType: 'WebPage',
    faqs: qualityFaqs
  },
  {
    path: '/production-supply',
    title: 'Production & Supply Discipline | Manufacturing Excellence | ZFAST',
    description:
      'Learn how ZFAST approaches raw material intake, production discipline, quality checks, packaging, storage, and dispatch readiness.',
    schemaType: 'WebPage'
  },
  {
    path: '/sustainability',
    title: 'Sustainability & Community | Responsible Agritech | ZFAST',
    description:
      'Explore ZFAST Agritech India sustainability, responsible growth, farmer support, careful communication, and local agriculture ecosystem support.',
    schemaType: 'WebPage'
  },
  {
    path: '/future-oils',
    title: 'Future Edible Oil Roadmap | Packaging & Product Range | ZFAST',
    description:
      'Explore ZFAST edible oil roadmap for consumer pack sizes, certifications, expanding oils portfolio, and bulk commercial contracts.',
    schemaType: 'WebPage',
    faqs: oilFaqs
  },
  {
    path: '/calculators/cattle',
    title: 'Cattle Weight Calculator | Heart Girth & Length Formula | ZFAST',
    description:
      'Estimate cattle and buffalo body weight accurately using heart girth and body length for precise feed planning, dosage, and BCS tracking.',
    schemaType: 'WebPage',
    faqs: cattleFaqs.slice(0, 3)
  },
  {
    path: '/calculators/pig',
    title: 'Pig Weight Calculator | Swine Body Weight Formula | ZFAST',
    description:
      'Estimate pig body weight quickly using heart girth and length measurements to calculate feed rations, medication doses, and market readiness.',
    schemaType: 'WebPage',
    faqs: pigFaqs
  },
  {
    path: '/for-our-people',
    title: 'For Our People | Workplace Safety & Farmer Community | ZFAST',
    description:
      'Learn how ZFAST Agritech India supports people, safe work, equal opportunities, community development, and responsible business growth.',
    schemaType: 'AboutPage'
  },
  {
    path: '/for-our-planet',
    title: 'For Our Planet | Environmental Responsibility & Resource Care | ZFAST',
    description:
      'Explore ZFAST Agritech India responsibility for resources, greener farming conversations, careful supply planning, and future-focused growth.',
    schemaType: 'AboutPage'
  },
  {
    path: '/for-our-performance',
    title: 'For Our Performance | Agritech Manufacturing Quality | ZFAST',
    description:
      'Learn how ZFAST Agritech India links performance with practical supply, quality discipline, farmer support, and responsible agritech growth.',
    schemaType: 'AboutPage'
  },
  {
    path: '/contact',
    title: 'Contact ZFAST Agritech India | Jaipur Office & Dealer Enquiries',
    description:
      'Contact ZFAST Agritech India in Jaipur, Rajasthan (+91 98120 13300) for cattle feed, pig feed, edible oil orders, and dealership enquiries.',
    schemaType: 'ContactPage',
    faqs: generalFaqs
  },
  {
    path: '/blog',
    title: 'Agritech Knowledge Hub & Farm Guides | ZFAST Agritech India',
    description:
      'Explore in-depth agritech guides on cattle feed nutrition, commercial pig farming FCR, Kachi Ghani mustard oil purity, aflatoxin prevention, and livestock weight calculation.',
    schemaType: 'CollectionPage',
    faqs: generalFaqs
  },
  ...blogPosts.map((post) => ({
    path: `/blog/${post.slug}`,
    title: post.metaTitle || post.title,
    description: post.metaDescription || post.summary,
    ogImage: post.coverImage,
    ogType: 'article' as const,
    schemaType: 'Article' as const,
    faqs: post.faqs,
    article: {
      publishedTime: post.publishedAt,
      modifiedTime: post.updatedAt,
      author: post.author.name,
      section: post.category,
      tags: post.keywords
    }
  }))
];

export const prerenderRoutes = seoRoutes.map((route) => route.path);

export function getSeoForPath(path: string) {
  const cleanPath = normalizePath(path);
  return seoRoutes.find((route) => route.path === cleanPath);
}

export function normalizePath(path: string) {
  if (!path || path === '/') return '/';
  return `/${path.replace(/^\/+|\/+$/g, '')}`;
}

export function absoluteUrl(path = '/') {
  const cleanPath = normalizePath(path);
  return cleanPath === '/' ? `${siteConfig.domain}/` : `${siteConfig.domain}${cleanPath}`;
}

export function absoluteAssetUrl(path: string) {
  if (path.startsWith('http')) return path;
  return `${siteConfig.domain}${path.startsWith('/') ? path : `/${path}`}`;
}
